import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const links = [
  { key: "nav.about", href: "#about" },
  { key: "nav.services", href: "#services" },
  { key: "nav.fleet", href: "#fleet" },
  { key: "nav.coverage", href: "#coverage" },
  { key: "nav.industries", href: "#industries" },
  { key: "nav.gallery", href: "/gallery", page: true },
  { key: "nav.careers", href: "/careers", page: true },
  { key: "nav.contact", href: "#contact" },
];

export const scrollToHash = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { lang, setLang, t } = useLang();
  const home = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSection = (e, href) => {
    setOpen(false);
    if (home) {
      e.preventDefault();
      scrollToHash(href);
    } else {
      e.preventDefault();
      window.location.href = `/${href}`;
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-navy/95 shadow-xl shadow-navy/30 backdrop-blur-xl" : "bg-navy/80 backdrop-blur-md"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-10">
        <Link to="/" data-testid="nav-logo" onClick={(e) => { if (home) { e.preventDefault(); scrollToHash("#hero"); } }} className="group flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center overflow-hidden rounded-full bg-white p-1 shadow-lg ring-2 ring-gold/60 transition-all duration-300 group-hover:ring-gold">
            <img src="/assets/logo.png" alt="MMGT Logo" className="h-full w-full object-contain" />
          </span>
          <span className="leading-tight">
            <span className="block font-jakarta text-base font-extrabold tracking-wide text-white">MANDAIR MOVERS</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.25em] text-sky/80">General Transport LLC</span>
          </span>
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {links.map((l) =>
            l.page ? (
              <Link key={l.key} to={l.href} data-testid={`nav-${l.key.replace("nav.", "")}`} className="font-jakarta text-sm font-semibold text-white/75 transition-colors duration-300 hover:text-gold">
                {t(l.key)}
              </Link>
            ) : (
              <a key={l.key} href={l.href} data-testid={`nav-${l.key.replace("nav.", "")}`} onClick={(e) => handleSection(e, l.href)} className="font-jakarta text-sm font-semibold text-white/75 transition-colors duration-300 hover:text-gold">
                {t(l.key)}
              </a>
            )
          )}
          <button
            data-testid="nav-lang-toggle"
            onClick={() => setLang(lang === "en" ? "ar" : "en")}
            className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-jakarta text-xs font-bold text-white/80 transition-all duration-300 hover:border-gold hover:text-gold"
          >
            <Languages className="h-4 w-4" /> {lang === "en" ? "عربي" : "EN"}
          </button>
          <a href="#contact" data-testid="nav-quote-btn" onClick={(e) => handleSection(e, "#contact")} className="btn-premium !px-6 !py-3">
            {t("nav.quote")}
          </a>
        </div>

        <button data-testid="nav-menu-toggle" aria-label="Toggle menu" onClick={() => setOpen(!open)} className="grid h-11 w-11 place-items-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur-md lg:hidden">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-white/10 bg-navy/95 px-6 py-6 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col gap-4">
            {links.map((l) =>
              l.page ? (
                <Link key={l.key} to={l.href} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.key.replace("nav.", "")}`} className="font-jakarta text-base font-semibold text-white">
                  {t(l.key)}
                </Link>
              ) : (
                <a key={l.key} href={l.href} onClick={(e) => handleSection(e, l.href)} data-testid={`nav-mobile-${l.key.replace("nav.", "")}`} className="font-jakarta text-base font-semibold text-white">
                  {t(l.key)}
                </a>
              )
            )}
            <button
              data-testid="nav-mobile-lang-toggle"
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="flex items-center gap-2 self-start rounded-full border border-white/20 px-4 py-2 font-jakarta text-xs font-bold text-white/80"
            >
              <Languages className="h-4 w-4" /> {lang === "en" ? "عربي" : "EN"}
            </button>
            <a href="#contact" data-testid="nav-mobile-quote-btn" onClick={(e) => handleSection(e, "#contact")} className="btn-premium mt-2 w-full">
              {t("nav.quote")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
